import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Input,
  SegmentedControl,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nexus/ui";

import type { ScriptDeliverySummary } from "@/lib/queries/script-delivery.queries";

const TARGET_MAX = 100_000;

export type ScriptDeliveryView = "month" | "client";

interface ScriptDeliveryPanelProps {
  summary: ScriptDeliverySummary;
  view: ScriptDeliveryView;
  onViewChange: (view: ScriptDeliveryView) => void;
  onCommitTarget: (tenantId: string, target: number | null) => void;
  savingTenantId: string | null;
}

function monthLabel(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function PercentValue({ percent }: { percent: number | null }) {
  const { t } = useTranslation();
  if (percent == null) {
    return <span aria-label={t("scriptDelivery.noTarget")}>—</span>;
  }
  return <span>{t("scriptDelivery.percentValue", { value: percent })}</span>;
}

function TargetField({
  name,
  value,
  disabled,
  onCommit,
}: {
  name: string;
  value: number | null;
  disabled: boolean;
  onCommit: (target: number | null) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value == null ? "" : String(value));

  useEffect(() => {
    setDraft(value == null ? "" : String(value));
  }, [value]);

  function commit() {
    const trimmed = draft.trim();
    if (trimmed === "") {
      if (value != null) onCommit(null);
      return;
    }
    if (!/^\d+$/.test(trimmed)) return;
    const next = Number(trimmed);
    if (next > TARGET_MAX) return;
    if (next !== value) onCommit(next);
  }

  return (
    <Input
      type="text"
      inputMode="numeric"
      aria-label={t("scriptDelivery.targetFor", { name })}
      value={draft}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
}

export function ScriptDeliveryPanel({
  summary,
  view,
  onViewChange,
  onCommitTarget,
  savingTenantId,
}: ScriptDeliveryPanelProps) {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            {t("scriptDelivery.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("scriptDelivery.subtitle", { month: monthLabel(summary.year, summary.month) })}
          </p>
        </div>
        <SegmentedControl
          ariaLabel={t("scriptDelivery.viewLabel")}
          accent="primary"
          value={view}
          onValueChange={(next) => onViewChange(next as ScriptDeliveryView)}
          options={[
            { value: "month", label: t("scriptDelivery.viewMonth") },
            { value: "client", label: t("scriptDelivery.viewClient") },
          ]}
        />
      </header>

      {view === "month" ? (
        <Card>
          <CardContent className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground">{t("scriptDelivery.delivered")}</p>
              <p className="text-2xl font-semibold tabular-nums text-foreground">{summary.delivered}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("scriptDelivery.submittedForFinalApproval")}</p>
              <p className="text-2xl font-semibold tabular-nums text-foreground">
                {summary.submittedForFinalApproval}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("scriptDelivery.target")}</p>
              <p className="text-2xl font-semibold tabular-nums text-foreground">{summary.target}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("scriptDelivery.percent")}</p>
              <p className="text-2xl font-semibold tabular-nums text-foreground">
                <PercentValue percent={summary.percent} />
              </p>
            </div>
          </CardContent>
        </Card>
      ) : summary.clients.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("scriptDelivery.empty")}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("scriptDelivery.viewClient")}</TableHead>
              <TableHead>{t("scriptDelivery.delivered")}</TableHead>
              <TableHead>{t("scriptDelivery.submittedForFinalApproval")}</TableHead>
              <TableHead>{t("scriptDelivery.target")}</TableHead>
              <TableHead>{t("scriptDelivery.percent")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.clients.map((client) => (
              <TableRow key={client.tenantId}>
                <TableCell>{client.name}</TableCell>
                <TableCell className="tabular-nums">{client.delivered}</TableCell>
                <TableCell className="tabular-nums">{client.submittedForFinalApproval}</TableCell>
                <TableCell className="w-36">
                  <TargetField
                    name={client.name}
                    value={client.target}
                    disabled={savingTenantId === client.tenantId}
                    onCommit={(target) => onCommitTarget(client.tenantId, target)}
                  />
                </TableCell>
                <TableCell className="tabular-nums">
                  <PercentValue percent={client.percent} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
